import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import React from 'react';
import { FormattedNumericInput } from 'routes/Risk/components/FormattedNumericInput';
import { TextInput } from 'routes/Risk/components/TextInput';
import {
  AssetClassType,
  emptySymbolTableRow,
  SymbolLimits,
  SymbolTableRow,
  UpdateOrCreateEntryRequest,
} from 'types/risk';

interface Props {
  readonly type: AssetClassType;
  readonly initialData?: SymbolTableRow;
  readonly readOnly: boolean;

  onClose(): void;
  onSubmit(request: UpdateOrCreateEntryRequest): void;
}

export const EditSymbolModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { type, initialData = emptySymbolTableRow, onClose, onSubmit, readOnly } = props;
  const [form, setForm] = React.useState<SymbolLimits>(initialData);

  const handleCreate = React.useCallback((): void => {
    if (readOnly) {
      return;
    }

    onSubmit({
      assetClassType: type,
      account: initialData.account,
      symbolLimits: form,
    });
  }, [form, initialData.account, onSubmit, readOnly, type]);

  const handleChange = React.useCallback(
    (name: keyof SymbolLimits, value: number | string | null): void => {
      if (readOnly) {
        return;
      }

      setForm((form: SymbolLimits): SymbolLimits => ({ ...form, [name]: value }));
    },
    [readOnly]
  );

  return (
    <Grid padding={2} direction="column" gap={2} container>
      <Grid item>
        <TextInput
          name="symbol"
          value={form.symbol}
          label="Symbol"
          readOnly={initialData === emptySymbolTableRow || readOnly}
          disabled={readOnly}
          onChange={handleChange}
        />
      </Grid>

      <Grid item>
        <FormattedNumericInput<SymbolLimits>
          name="maxqty"
          value={form.maxqty}
          label="Max Quantity"
          disabled={readOnly}
          onChange={handleChange}
        />
      </Grid>

      <Grid item>
        <FormattedNumericInput<SymbolLimits>
          name="maxpercentaway"
          value={form.maxpercentaway}
          label="Max Price (% Away)"
          disabled={readOnly}
          onChange={handleChange}
        />
      </Grid>

      <Grid item>
        <FormattedNumericInput
          name="maxnotional"
          value={form.maxnotional}
          label="Max Notional"
          disabled={readOnly}
          onChange={handleChange}
        />
      </Grid>

      <Grid item>
        <FormattedNumericInput
          name="commrate"
          value={form.commrate}
          label="Comm Rate"
          disabled={readOnly}
          onChange={handleChange}
        />
      </Grid>

      <Grid display="flex" alignItems="center" justifyContent="end" marginTop={2} gap={2}>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={readOnly}>
          {initialData === emptySymbolTableRow ? 'Add' : 'Update'}
        </Button>
      </Grid>
    </Grid>
  );
};
